/**
 * Chart adaptation utilities for low-cardinality time-series data.
 *
 * When daily data volume is sparse (typical for security incidents/investigations),
 * standard line/area charts render poorly — mostly flat lines with isolated dots.
 * These utilities detect sparse data and transform it for better chart types.
 */

import type { ChartDataPoint } from '@/services/analytics.service';

/** Density classification for time-series data */
export type DataDensity = 'sparse' | 'medium' | 'dense';

/** Recommended chart type based on data density */
export type RecommendedChartType =
  | 'weeklyBar'
  | 'periodComparison'
  | 'cumulativeLine'
  | 'originalLine'
  | 'originalArea';

/** Weekly aggregated data point for bar charts */
export interface WeeklyDataPoint {
  weekLabel: string;
  weekStart: string;
  total: number;
}

/** Period comparison data for two-bar charts */
export interface PeriodComparisonData {
  currentPeriodTotal: number;
  previousPeriodTotal: number;
  currentPeriodLabel: string;
  previousPeriodLabel: string;
  changePercent: number;
}

/** Thresholds for density classification */
const SPARSE_THRESHOLD = 2;
const DENSE_THRESHOLD = 10;

/** Parse a YYYY-MM-DD string as a local date (avoids timezone shift from new Date()) */
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Format a local Date as YYYY-MM-DD */
function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Compute the average daily count from time-series data.
 * Returns the density classification and the raw average.
 */
export function computeDataDensity(data: ChartDataPoint[]): {
  density: DataDensity;
  averageDailyCount: number;
  totalDays: number;
  totalValue: number;
} {
  if (data.length === 0) {
    return { density: 'sparse', averageDailyCount: 0, totalDays: 0, totalValue: 0 };
  }

  const totalValue = data.reduce((sum, d) => sum + d.value, 0);
  const totalDays = data.length;
  const averageDailyCount = totalValue / totalDays;

  let density: DataDensity;
  if (averageDailyCount < SPARSE_THRESHOLD) {
    density = 'sparse';
  } else if (averageDailyCount < DENSE_THRESHOLD) {
    density = 'medium';
  } else {
    density = 'dense';
  }

  return { density, averageDailyCount, totalDays, totalValue };
}

/**
 * Return the recommended chart type for the given time-series data.
 *
 * - sparse (<2/day avg): weekly bars
 * - medium (2–10/day avg): cumulative line
 * - dense (>10/day avg): keep original line/area
 */
export function getRecommendedChartType(
  data: ChartDataPoint[],
  originalType: 'line' | 'area' = 'area'
): RecommendedChartType {
  const { density, totalDays } = computeDataDensity(data);

  if (density === 'sparse') {
    // For very short ranges, period comparison is more useful
    if (totalDays <= 14) {
      return 'periodComparison';
    }
    return 'weeklyBar';
  }

  if (density === 'medium') {
    return 'cumulativeLine';
  }

  return originalType === 'line' ? 'originalLine' : 'originalArea';
}

/**
 * Aggregate daily data points into weekly buckets.
 * Each bucket sums all values for that ISO week.
 */
export function aggregateToWeekly(data: ChartDataPoint[]): WeeklyDataPoint[] {
  if (data.length === 0) {return [];}

  const weekMap = new Map<string, { weekStart: string; total: number }>();

  for (const point of data) {
    const date = parseLocalDate(point.date);
    // Get Monday of the week (ISO week start)
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    const weekKey = formatDateKey(monday);

    const existing = weekMap.get(weekKey);
    if (existing) {
      existing.total += point.value;
    } else {
      weekMap.set(weekKey, { weekStart: weekKey, total: point.value });
    }
  }

  // Sort by week start date and format labels
  return Array.from(weekMap.values())
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart))
    .map((week) => {
      const start = parseLocalDate(week.weekStart);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);

      const formatShort = (d: Date) =>
        `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;

      return {
        weekLabel: `${formatShort(start)} – ${formatShort(end)}`,
        weekStart: week.weekStart,
        total: week.total,
      };
    });
}

/**
 * Convert daily data into a cumulative line.
 * Each point's value is the running sum of all previous points.
 */
export function toCumulativeLine(data: ChartDataPoint[]): ChartDataPoint[] {
  if (data.length === 0) {return [];}

  let cumulative = 0;
  return data.map((point) => {
    cumulative += point.value;
    return { ...point, value: cumulative };
  });
}

/**
 * Split data into two halves and compare totals.
 * Useful for "this period vs. previous period" bar charts.
 */
export function toPeriodComparison(data: ChartDataPoint[]): PeriodComparisonData {
  if (data.length === 0) {
    return {
      currentPeriodTotal: 0,
      previousPeriodTotal: 0,
      currentPeriodLabel: 'Current',
      previousPeriodLabel: 'Previous',
      changePercent: 0,
    };
  }

  const midpoint = Math.ceil(data.length / 2);
  const previousHalf = data.slice(0, midpoint);
  const currentHalf = data.slice(midpoint);

  const previousPeriodTotal = previousHalf.reduce((sum, d) => sum + d.value, 0);
  const currentPeriodTotal = currentHalf.reduce((sum, d) => sum + d.value, 0);

  const changePercent =
    previousPeriodTotal > 0
      ? ((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100
      : 0;

  const formatDate = (d: ChartDataPoint) => {
    const date = parseLocalDate(d.date);
    return `${date.toLocaleString('default', { month: 'short' })} ${date.getDate()}`;
  };

  const previousPeriodLabel =
    previousHalf.length > 0
      ? `${formatDate(previousHalf[0])} – ${formatDate(previousHalf[previousHalf.length - 1])}`
      : 'Previous';
  const currentPeriodLabel =
    currentHalf.length > 0
      ? `${formatDate(currentHalf[0])} – ${formatDate(currentHalf[currentHalf.length - 1])}`
      : 'Current';

  return {
    currentPeriodTotal,
    previousPeriodTotal,
    currentPeriodLabel,
    previousPeriodLabel,
    changePercent,
  };
}
